/**
 * errorBuffer.js
 *
 * Circular buffer (max 5 entries) that captures unhandled JS errors and
 * unhandled promise rejections. Used by the report context builder to
 * include recent console errors in the report payload.
 *
 * Usage:
 *   initErrorBuffer()   — call once at app startup (index.js)
 *   getErrorBuffer()    — returns the current captured errors (array)
 */

const MAX_BUFFER = 5;

let _errors = [];
let _initialized = false;

/**
 * Initializes the error buffer listeners.
 * Safe to call multiple times — only installs listeners once.
 */
export function initErrorBuffer() {
  if (_initialized) return;
  _initialized = true;

  // Unhandled JS errors
  window.addEventListener('error', (event) => {
    _pushError({
      type: 'error',
      message: event.message || String(event),
      source: event.filename || null,
      line: event.lineno || null,
      col: event.colno || null,
      timestamp: new Date().toISOString(),
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    _pushError({
      type: 'unhandledrejection',
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? (reason.stack || null) : null,
      timestamp: new Date().toISOString(),
    });
  });
}

/**
 * Returns a copy of the current error buffer (up to 5 entries).
 * @returns {Array<Object>}
 */
export function getErrorBuffer() {
  return [..._errors];
}

/**
 * Pushes a new error to the circular buffer, evicting oldest if full.
 * @param {Object} entry
 */
function _pushError(entry) {
  if (_errors.length >= MAX_BUFFER) {
    _errors.shift();
  }
  _errors.push(entry);
}
